/**
 * Integration Utilities
 * 
 * Utility functions for integration management, authentication,
 * data transformation, and error handling
 */

import { 
  IntegrationType, 
  IntegrationStatus, 
  SyncFrequency, 
  GMBLocationStatus,
  GMBPostType,
  AlertSeverity,
  IntegrationConfig,
  AnalyticsIntegration,
  WordPressIntegration 
} from '@/types/integrations'
import type { 
  JobberEstimateStatus,
  JobberIntegration,
  JobberInvoiceStatus,
  JobberJobStatus
} from '@/features/jobber/types'

// Integration Type Mappings
export const integrationTypeLabels: Record<IntegrationType, string> = {
  jobber_crm: 'Jobber CRM',
  google_my_business: 'Google My Business',
  google_analytics: 'Google Analytics',
  google_search_console: 'Google Search Console',
  facebook_pixel: 'Facebook Pixel',
  wordpress: 'WordPress',
  social_media: 'Social Media',
}

export const integrationTypeDescriptions: Record<IntegrationType, string> = {
  jobber_crm: 'Sync customer data, jobs, estimates, and invoices with Jobber CRM',
  google_my_business: 'Manage business listings, reviews, and posts across Google My Business',
  google_analytics: 'Track website traffic, conversions, and user behavior',
  google_search_console: 'Monitor search performance, indexing, and SEO metrics',
  facebook_pixel: 'Track conversions and build audiences for Facebook advertising',
  wordpress: 'Migrate content, media, and data from WordPress sites',
  social_media: 'Manage social media accounts and automate posting',
}

export const integrationTypeIcons: Record<IntegrationType, string> = {
  jobber_crm: '🔧',
  google_my_business: '🏢',
  google_analytics: '📊',
  google_search_console: '🔍',
  facebook_pixel: '📱',
  wordpress: '📝',
  social_media: '📱',
}

// Status Mappings
export const integrationStatusLabels: Record<IntegrationStatus, string> = {
  disconnected: 'Disconnected',
  connecting: 'Connecting',
  connected: 'Connected',
  syncing: 'Syncing',
  error: 'Error',
  suspended: 'Suspended',
}

export const integrationStatusColors: Record<IntegrationStatus, string> = {
  disconnected: 'gray',
  connecting: 'yellow',
  connected: 'green',
  syncing: 'blue',
  error: 'red',
  suspended: 'orange',
}

export const syncFrequencyLabels: Record<SyncFrequency, string> = {
  real_time: 'Real-time',
  every_15_minutes: 'Every 15 minutes',
  hourly: 'Hourly',
  daily: 'Daily',
  weekly: 'Weekly',
  manual: 'Manual only',
}

// Jobber Utilities
export const jobberStatusLabels: Record<JobberJobStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

export const jobberStatusColors: Record<JobberJobStatus, string> = {
  draft: 'gray',
  scheduled: 'blue',
  in_progress: 'yellow',
  completed: 'green',
  cancelled: 'red',
}

export const jobberEstimateStatusLabels: Record<JobberEstimateStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  approved: 'Approved',
  declined: 'Declined',
  expired: 'Expired',
}

export const jobberInvoiceStatusLabels: Record<JobberInvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Viewed',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
}

// GMB Utilities
export const gmbLocationStatusLabels: Record<GMBLocationStatus, string> = {
  VERIFIED: 'Verified',
  UNVERIFIED: 'Unverified',
  VERIFICATION_REQUESTED: 'Verification Requested',
  SUSPENDED: 'Suspended',
}

export const gmbPostTypeLabels: Record<GMBPostType, string> = {
  WHAT_IS_NEW: 'What\'s New',
  EVENT: 'Event',
  OFFER: 'Offer',
  PRODUCT: 'Product',
}

// Validation Functions
export function validateIntegrationConfig(config: Partial<IntegrationConfig>): {
  isValid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (!config.name?.trim()) {
    errors.push('Integration name is required')
  }

  if (!config.type) {
    errors.push('Integration type is required')
  }

  // Type-specific validation
  switch (config.type) {
    case 'jobber_crm': {
      const jobberConfig = config as Partial<JobberIntegration>
      if (!jobberConfig.credentials?.client_id) {
        errors.push('Jobber Client ID is required')
      }
      if (!jobberConfig.settings?.franchise_region) {
        errors.push('Franchise region is required for Jobber integration')
      }
      break
    }

    case 'google_analytics':
    case 'google_search_console': {
      const analyticsConfig = config as Partial<AnalyticsIntegration>
      if (!analyticsConfig.credentials?.property_id) {
        errors.push('Property ID is required for Google Analytics integration')
      }
      break
    }

    case 'facebook_pixel': {
      const pixelConfig = config as Partial<AnalyticsIntegration>
      if (!pixelConfig.credentials?.pixel_id) {
        errors.push('Pixel ID is required for Facebook Pixel integration')
      }
      break
    }

    case 'wordpress': {
      const wpConfig = config as Partial<WordPressIntegration>
      if (!wpConfig.credentials?.site_url) {
        errors.push('WordPress site URL is required')
      }
      if (!isValidUrl(wpConfig.credentials?.site_url || '')) {
        errors.push('WordPress site URL must be a valid URL')
      }
      break
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

// Data Transformation Functions
export function formatIntegrationName(type: IntegrationType, name?: string): string {
  if (name) return name
  return integrationTypeLabels[type] || type
}

export function getIntegrationHealthScore(
  errorCount: number,
  lastSync?: string,
  uptimePercentage?: number
): number {
  let score = 100

  // Deduct points for errors
  score -= Math.min(errorCount * 10, 50)

  // Deduct points for stale sync
  if (lastSync) {
    const lastSyncDate = new Date(lastSync)
    const hoursSinceSync = (Date.now() - lastSyncDate.getTime()) / (1000 * 60 * 60)
    
    if (hoursSinceSync > 168) { // More than a week
      score -= 30
    } else if (hoursSinceSync > 24) { // More than a day
      score -= 15
    } else if (hoursSinceSync > 1) { // More than an hour
      score -= 5
    }
  } else {
    score -= 20 // Never synced
  }

  // Factor in uptime percentage
  if (uptimePercentage !== undefined) {
    score = score * (uptimePercentage / 100)
  }

  return Math.max(0, Math.round(score))
}

export function getAlertSeverityColor(severity: AlertSeverity): string {
  const colors = {
    low: 'green',
    medium: 'yellow',
    high: 'orange',
    critical: 'red',
  }
  return colors[severity]
}

// OAuth and Authentication Utilities
export function buildOAuthUrl(
  type: IntegrationType,
  clientId: string,
  redirectUri: string,
  state?: string
): string {
  const baseUrls = {
    jobber_crm: 'https://api.getjobber.com/api/oauth/authorize',
    google_my_business: 'https://accounts.google.com/o/oauth2/v2/auth',
    google_analytics: 'https://accounts.google.com/o/oauth2/v2/auth',
    google_search_console: 'https://accounts.google.com/o/oauth2/v2/auth',
    facebook_pixel: 'https://www.facebook.com/v18.0/dialog/oauth',
    wordpress: '', // WordPress uses different auth methods
    social_media: '',
  }

  const scopes = {
    jobber_crm: 'read_jobs read_clients read_invoices',
    google_my_business: 'https://www.googleapis.com/auth/business.manage',
    google_analytics: 'https://www.googleapis.com/auth/analytics.readonly',
    google_search_console: 'https://www.googleapis.com/auth/webmasters.readonly',
    facebook_pixel: 'ads_management ads_read',
    wordpress: '',
    social_media: '',
  }

  const baseUrl = baseUrls[type]
  if (!baseUrl) return ''

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: scopes[type] || '',
  })

  if (state) {
    params.append('state', state)
  }

  return `${baseUrl}?${params.toString()}`
}

export function parseOAuthCallback(url: string): {
  code?: string
  error?: string
  state?: string
} {
  const urlObj = new URL(url)
  const params = urlObj.searchParams

  return {
    code: params.get('code') || undefined,
    error: params.get('error') || undefined,
    state: params.get('state') || undefined,
  }
}

// Data Formatting Functions
export function formatCurrency(amount: number, currency = 'CAD'): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
  }).format(amount)
}

export function formatPhoneNumber(phone: string): string {
  // Simple Canadian phone number formatting
  const cleaned = phone.replace(/\D/g, '')
  
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  
  return phone
}

export function formatAddress(address: {
  street?: string
  city?: string
  province?: string
  postal_code?: string
  country?: string
}): string {
  const parts = [
    address.street,
    address.city,
    address.province,
    address.postal_code,
    address.country,
  ].filter(Boolean)
  
  return parts.join(', ')
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`
  }
  
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  
  if (remainingMinutes === 0) {
    return `${hours}h`
  }
  
  return `${hours}h ${remainingMinutes}m`
}

// Date and Time Utilities
export function formatRelativeTime(date: string | Date): string {
  const now = new Date()
  const targetDate = typeof date === 'string' ? new Date(date) : date
  const diffInMs = now.getTime() - targetDate.getTime()
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
  const diffInHours = Math.floor(diffInMinutes / 60)
  const diffInDays = Math.floor(diffInHours / 24)

  if (diffInMinutes < 1) {
    return 'Just now'
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`
  } else {
    return targetDate.toLocaleDateString('en-CA')
  }
}

export function getNextSyncTime(
  lastSync: string | undefined,
  frequency: SyncFrequency
): Date | null {
  if (frequency === 'manual' || !lastSync) {
    return null
  }

  const lastSyncDate = new Date(lastSync)
  const intervals = {
    real_time: 0,
    every_15_minutes: 15 * 60 * 1000,
    hourly: 60 * 60 * 1000,
    daily: 24 * 60 * 60 * 1000,
    weekly: 7 * 24 * 60 * 60 * 1000,
  }

  const interval = intervals[frequency]
  return new Date(lastSyncDate.getTime() + interval)
}

// Integration Feature Detection
export function getIntegrationFeatures(type: IntegrationType): string[] {
  const features: Record<IntegrationType, string[]> = {
    jobber_crm: [
      'Customer Synchronization',
      'Job Management',
      'Invoice Tracking',
      'Estimate Management',
      'Lead Auto-Creation',
      'Two-way Data Sync',
    ],
    google_my_business: [
      'Multi-location Management',
      'Review Monitoring',
      'Automated Responses',
      'Post Scheduling',
      'Performance Analytics',
      'Business Hours Management',
    ],
    google_analytics: [
      'Traffic Monitoring',
      'Conversion Tracking',
      'Custom Dimensions',
      'Goal Setting',
      'Automated Reporting',
      'Real-time Data',
    ],
    google_search_console: [
      'Search Performance',
      'Indexing Status',
      'SEO Monitoring',
      'Keyword Tracking',
      'Technical SEO',
      'Search Appearance',
    ],
    facebook_pixel: [
      'Conversion Tracking',
      'Audience Building',
      'Custom Events',
      'Attribution Modeling',
      'Campaign Optimization',
      'Retargeting Setup',
    ],
    wordpress: [
      'Content Migration',
      'Media Transfer',
      'URL Preservation',
      'SEO Data Migration',
      'Comment Migration',
      'Database Transfer',
    ],
    social_media: [
      'Multi-platform Posting',
      'Content Scheduling',
      'Engagement Tracking',
      'Hashtag Management',
      'Account Management',
      'Analytics Integration',
    ],
  }

  return features[type] || []
}

// Error Handling Utilities
export function categorizeIntegrationError(error: string): {
  category: 'auth' | 'network' | 'rate_limit' | 'data' | 'configuration' | 'unknown'
  severity: AlertSeverity
  suggestion: string
} {
  const errorLower = error.toLowerCase()

  if (errorLower.includes('unauthorized') || errorLower.includes('auth')) {
    return {
      category: 'auth',
      severity: 'high',
      suggestion: 'Check authentication credentials and refresh tokens',
    }
  }

  if (errorLower.includes('rate limit') || errorLower.includes('quota')) {
    return {
      category: 'rate_limit',
      severity: 'medium',
      suggestion: 'Reduce sync frequency or contact the service provider',
    }
  }

  if (errorLower.includes('network') || errorLower.includes('timeout')) {
    return {
      category: 'network',
      severity: 'medium',
      suggestion: 'Check network connectivity and try again',
    }
  }

  if (errorLower.includes('validation') || errorLower.includes('invalid')) {
    return {
      category: 'data',
      severity: 'low',
      suggestion: 'Review data format and validation requirements',
    }
  }

  if (errorLower.includes('config') || errorLower.includes('setting')) {
    return {
      category: 'configuration',
      severity: 'medium',
      suggestion: 'Review integration settings and configuration',
    }
  }

  return {
    category: 'unknown',
    severity: 'medium',
    suggestion: 'Contact support for assistance with this error',
  }
}

// Export utility object for easy import
export const integrationUtils = {
  typeLabels: integrationTypeLabels,
  typeDescriptions: integrationTypeDescriptions,
  typeIcons: integrationTypeIcons,
  statusLabels: integrationStatusLabels,
  statusColors: integrationStatusColors,
  syncFrequencyLabels,
  validateConfig: validateIntegrationConfig,
  formatName: formatIntegrationName,
  getHealthScore: getIntegrationHealthScore,
  buildOAuthUrl,
  parseOAuthCallback,
  formatCurrency,
  formatPhoneNumber,
  formatAddress,
  formatDuration,
  formatRelativeTime,
  getNextSyncTime,
  getFeatures: getIntegrationFeatures,
  categorizeError: categorizeIntegrationError,
}
